import { PauseCircleOutlined, PlayCircleOutlined, PrinterOutlined } from '@ant-design/icons'
import { Button, notification } from 'antd'
import dayjs from 'dayjs'
import { FC, useContext } from 'react'
import connect from '../../../../../helpers/api/connect'
import useErrorModal from '../../../../../hooks/api/use-error-modal'
import { userStorage } from '../../../../../services/user-storage'
import { useCreatePrintJobExecutorMutation } from '../../../../../store/api/print-job-executors-api'
import { useUpdatePrintJobMutation } from '../../../../../store/api/print-jobs-api'
import { printQueueEntriesApi } from '../../../../../store/api/print-queue-entries-api'
import { useAppDispatch } from '../../../../../store/store'
import { JobStatuses } from '../../../../../types/api/print-job'
import PrintJobExecutor from '../../../../../types/api/print-job-executor'
import PrintQueueEntry from '../../../../../types/api/print-queue-entry'
import {
    isCurrentUserActiveExecutor,
    isPrintJobActive,
    isPrintJobInProgress,
    isPrintJobPaused,
} from '../helpers/print-job-helpers'
import { OneMachineScheduleContext } from '../one-machine-schedule'

interface StartPrintProps {
    printQueueEntry: PrintQueueEntry
}

const StartPrint: FC<StartPrintProps> = ({ printQueueEntry }) => {
    const dispatch = useAppDispatch()
    const machineQueue = useContext(OneMachineScheduleContext)
    const printJob = printQueueEntry.print_job
    const currentUserDocumentId = userStorage.get()?.documentId

    const [updatePrintJob, updatePrintJobState] = useUpdatePrintJobMutation()
    const [createPrintJobExecutor, createPrintJobExecutorState] =
        useCreatePrintJobExecutorMutation()

    const isCurrentUserActive = isCurrentUserActiveExecutor(printJob, currentUserDocumentId)
    const isPaused = isPrintJobPaused(printJob)
    const isActive = isPrintJobActive(printJob)

    const machineHasUnfinishedPrint = machineQueue.some(entry =>
        isPrintJobInProgress(entry.print_job)
    )
    const isLoading = updatePrintJobState.isLoading || createPrintJobExecutorState.isLoading

    const connectExecutorToPrintJob = async (executor: PrintJobExecutor, isContinue: boolean) => {
        const result = await updatePrintJob({
            docId: printJob!.documentId,
            body: {
                data: {
                    ...(isContinue ? {} : { jobStatus: JobStatuses.Printing }),
                    print_job_executors: connect(executor),
                },
            },
        })

        if (result.data) {
            dispatch(printQueueEntriesApi.util.invalidateTags(['PrintQueueEntries']))
            notification.success({
                message: isContinue ? 'Печать продолжена' : 'Печать начата',
            })
        }
    }

    const onCreateExecutor = async (isContinue: boolean) => {
        const result = await createPrintJobExecutor({
            data: {
                user: { connect: [currentUserDocumentId || ''] },
                machine: { connect: [printQueueEntry.machine?.documentId || ''] },
                start: dayjs().toISOString(),
            },
        })

        if (result.data) {
            await connectExecutorToPrintJob(result.data.data, isContinue)
        }
    }

    useErrorModal(createPrintJobExecutorState, 'Ошибка создания записи о печати')
    useErrorModal(updatePrintJobState, 'Ошибка обновления задания печати')

    if (isCurrentUserActive) {
        return (
            <Button
                style={{ width: '118px' }}
                icon={<PrinterOutlined />}
                variant={'solid'}
                color={'green'}
            >
                Печатается
            </Button>
        )
    }

    if (isPaused) {
        return (
            <Button
                icon={<PlayCircleOutlined />}
                type={'primary'}
                loading={isLoading}
                onClick={() => onCreateExecutor(true)}
            >
                Продолжить печать
            </Button>
        )
    }

    if (isActive) {
        return (
            <Button style={{ width: '118px' }} icon={<PauseCircleOutlined />} disabled>
                Занято
            </Button>
        )
    }

    return (
        <Button
            icon={<PlayCircleOutlined />}
            type={'primary'}
            disabled={machineHasUnfinishedPrint}
            loading={isLoading}
            onClick={() => onCreateExecutor(false)}
        >
            Начать печать
        </Button>
    )
}

export default StartPrint
